import cx from 'clsx';
import React, { useState } from 'react';

import { ReactComponent as FilterIcon } from '@assets/svg/filter.svg';
import { ReactComponent as SearchIcon } from '@assets/svg/search.svg';
import { ReactComponent as StatisticIcon } from '@assets/svg/statistic.svg';

import ResetButton from '@components/common/ResetButton/ResetButton';
import DatePicker from '@components/form/DatePicker/DatePicker';
import FilterSearch from '@components/uikit/FilterSearch/FilterSearch';
import Select from '@components/uikit/Select/Select';
import Switch from '@components/uikit/Switch/Switch';

import { troubleshootingHoldOption } from '@constants/troubleshootingHoldOption';

import { getAITypesList } from '@helpers/getAITypesList';

import { useDebouncedValue } from '@hooks/useDebouncedValue';
import useTranslation from '@hooks/useTranslation';

import { siteEngagementTooltipIds } from '@pages/SiteEngagement/constants/siteEngagementTooltipIds';

import { useUserProfileQuery } from '@services/user/useUserProfileQuery';

import { selectAppLocationSelectValue, selectAppRtl } from '@store/useAppStore';

import { initialSorting } from '../../constants/initialSortingTotalStatistics';
import { getUrgenciesList } from '../../helpers/getUrgenciesList';
import { isQueryEnabled } from '../../helpers/isQueryEnabled';
import { onChangeSorting } from '../../helpers/onChangeSorting';
import { useEngagementStatsFacilityQuery } from '../../services/useEngagementStatsFacilityQuery';
import {
  selectEngagementAdditionalFilterIsChangedValue,
  selectEngagementFilterCartRangeValue,
  selectEngagementFilterIsChangedValue,
  selectEngagementFilterReportFailure,
  selectEngagementFilterSearchValue,
  selectEngagementFilterState,
  selectEngagementFilterStatesValue,
  selectEngagementFilterTimeCreatedRangeValue,
  selectEngagementFilterTypeValue,
  selectEngagementFilterUrgencyValue,
  selectEngagementSmallWidthAdditionalFilterIsChangedValue,
  useEngagementFilterActions,
} from '../../store/useEngagementFilterStore';
import AdditionalFilters from '../AdditionalFilters/AdditionalFilters';
import TableTotalStatistics from '../TableTotalStatistics/TableTotalStatistics';
import style from './style.module.scss';

const FilterPanel = () => {
  const { t } = useTranslation();
  const {
    setEngagementFilterSearchValue,
    setEngagementFilterTimeCreatedRangeValue,
    setEngagementFilterUrgencyValue,
    setEngagementFilterTypeValue,
    setEngagementFilterInitialState,
    setEngagementFilterReportFailure,
  } = useEngagementFilterActions();

  const rtl = selectAppRtl();
  const locationSelectValue = selectAppLocationSelectValue();
  const searchValue = selectEngagementFilterSearchValue();
  const { minTimeCreated, maxTimeCreated } = selectEngagementFilterTimeCreatedRangeValue();
  const urgency = selectEngagementFilterUrgencyValue();
  const type = selectEngagementFilterTypeValue();
  const filter = selectEngagementFilterState();
  const isFiltersChanged = selectEngagementFilterIsChangedValue();
  const isSmallWidthAdditionalFilterChanged = selectEngagementSmallWidthAdditionalFilterIsChangedValue();
  const isAdditionalFilterChanged = selectEngagementAdditionalFilterIsChangedValue();
  const reportedFailure = selectEngagementFilterReportFailure();
  const cartRange = selectEngagementFilterCartRangeValue();
  const states = selectEngagementFilterStatesValue();

  const typesList = getAITypesList(t);

  const [sorting, setSorting] = useState(initialSorting);
  const [showAdditionalFilter, setShowAdditionalFilter] = useState(false);
  const [showStatistic, setShowStatistic] = useState(!searchValue);

  const [debouncedCartRangeValueValue] = useDebouncedValue(cartRange, 500);

  const { data: profileData } = useUserProfileQuery();

  const {
    data: engagementStatsFacilityData,
    isLoading: engagementStatsFacilityIsLoading,
    isRefetching: engagementStatsFacilityIsRefetching,
  } = useEngagementStatsFacilityQuery({
    filter,
    cartRange: debouncedCartRangeValueValue,
    locationIds:
      locationSelectValue.level_name === 'allFacilities' ? undefined : locationSelectValue?.filterIds,
    sorting,
    enabled: isQueryEnabled(filter),
  });

  const clearFilter = () => {
    setEngagementFilterInitialState();
  };

  const onChangeDateRange = (dates: [Date | null, Date | null]) => {
    setEngagementFilterTimeCreatedRangeValue({
      minTimeCreated: dates[0],
      maxTimeCreated: dates[1],
    });
  };

  const urgenciesList = getUrgenciesList(engagementStatsFacilityData?.filter_data.urgencies || [], t);
  const actionListStates = [
    ...(engagementStatsFacilityData?.filter_data.states || []),
    { name: t(troubleshootingHoldOption.name), id: troubleshootingHoldOption.id },
  ];

  const formattedMinTimeCreated = minTimeCreated ? new Date(minTimeCreated) : undefined;
  const formattedMaxTimeCreated = maxTimeCreated ? new Date(maxTimeCreated) : undefined;

  const { filter_data } = engagementStatsFacilityData || {};
  const filterLimits = {
    minCart: filter_data?.min_cart ?? 0,
    maxCart: filter_data?.max_cart ?? 0,
    minTimeCreated: new Date(filter_data?.min_time_created ?? Date.now()),
    maxTimeCreated: new Date(filter_data?.max_time_created ?? Date.now()),
  };

  const isAllStatusesSelected = states.length >= actionListStates.length;

  const showResetFilterBtn =
    isFiltersChanged ||
    type.value !== 'all' ||
    urgency.value !== 'all' ||
    !!reportedFailure ||
    !isAllStatusesSelected;

  const showExtraFiltersIcon = isAdditionalFilterChanged || !isAllStatusesSelected;
  const showSmallWidthExtraFiltersIcon =
    showExtraFiltersIcon || isSmallWidthAdditionalFilterChanged || !!reportedFailure;

  return (
    <div className={style.engagementFilterBlock}>
      <div className={style.engagementFilterBlockContainer}>
        <div className={style.filterFields}>
          <div className={style.leftFilterFields}>
            <div className={style.filterFieldContainer}>
              <Select
                value={type}
                onChange={setEngagementFilterTypeValue}
                options={typesList}
                label={t('Action Item Type')}
                itemKeys={{ valueFieldKey: 'value', labelFieldKey: 'label' }}
                type="woHelperText"
                dataQa="type"
              />
            </div>
            <div className={style.filterFieldContainer}>
              <Select
                value={urgency.value === 'all' ? urgenciesList[0] : urgency}
                onChange={setEngagementFilterUrgencyValue}
                options={urgenciesList}
                label={t('Urgency')}
                itemKeys={{ valueFieldKey: 'value', labelFieldKey: 'label' }}
                type="woHelperText"
                dataQa="urgency"
              />
            </div>
            <div className={cx(style.filterFieldContainer, style.smallWidthHide)}>
              <DatePicker
                id="daterange-engagement"
                label={t('Action Item Update Date Range')}
                placeholderText={t('Select Date Range')}
                dateFormat={rtl ? 'yyyy/MM/dd' : `${profileData?.dateFormat.date}`}
                startDate={formattedMinTimeCreated}
                endDate={formattedMaxTimeCreated}
                minDate={filterLimits.minTimeCreated}
                onChange={onChangeDateRange}
                selectsRange
                showMonthDropdown
                showYearDropdown
                autoComplete="off"
                type="woHelperText"
              />
            </div>
            <div className={style.additionalFiltersContainer}>
              <div className={style.smallWidthHide}>
                <Switch
                  value={!!reportedFailure}
                  onChange={() => setEngagementFilterReportFailure(!reportedFailure)}
                  label={t('Customer - Reported Failures')}
                  type="woHelperText"
                />
              </div>
              <div
                className={style.additionalFiltersMenu}
                data-tooltip-id="site-engagement-additional-filters"
                onClick={() => setShowAdditionalFilter(true)}
              >
                <div
                  className={cx(style.iconContainer, style.filterIcon, {
                    [style.active]: showAdditionalFilter,
                  })}
                  data-tooltip-content={t('View Additional Filters')}
                  data-tooltip-id={siteEngagementTooltipIds.LONG_TEXT_TOOLTIP_ID}
                >
                  <span
                    className={cx({
                      [style.activeExtraFiltersIconDot]: showExtraFiltersIcon,
                      [style.activeSmallWidthExtraFiltersIconDot]: showSmallWidthExtraFiltersIcon,
                    })}
                  >
                    <FilterIcon className={style.threeDotsIcon} />
                  </span>
                </div>
              </div>
              <div className={style.resetWrapper}>
                <ResetButton showResetButton={showResetFilterBtn} onClick={clearFilter} />
              </div>
            </div>
          </div>
          <div className={style.rightFilterFields}>
            <div
              className={cx(style.iconContainer, { [style.active]: showStatistic })}
              onClick={() => {
                setShowStatistic(true);
                setEngagementFilterSearchValue('');
              }}
              data-tooltip-content={t('General Statistics Panel')}
              data-tooltip-id={siteEngagementTooltipIds.LONG_TEXT_TOOLTIP_ID}
            >
              <StatisticIcon className={style.statisticIcon} />
            </div>
            <div
              className={cx(style.iconContainer, { [style.active]: !showStatistic })}
              onClick={() => setShowStatistic(false)}
              data-tooltip-content={t('Search Bar')}
              data-tooltip-id={siteEngagementTooltipIds.LONG_TEXT_TOOLTIP_ID}
            >
              <SearchIcon className={style.searchIcon} />
            </div>
          </div>
        </div>
        {showStatistic ? (
          <TableTotalStatistics
            data={engagementStatsFacilityData}
            isLoading={engagementStatsFacilityIsLoading || engagementStatsFacilityIsRefetching}
            sorting={sorting}
            onChangeSorting={(key) => onChangeSorting(key, sorting, initialSorting, setSorting)}
          />
        ) : (
          <div className={style.searchPanel}>
            <FilterSearch
              value={searchValue}
              onChange={setEngagementFilterSearchValue}
              placeholder={t('Start to type...')}
              variant="header-search"
            />
          </div>
        )}
      </div>

      <AdditionalFilters
        id="site-engagement-additional-filters"
        visible={showAdditionalFilter}
        minTimeCreated={formattedMinTimeCreated}
        maxTimeCreated={formattedMaxTimeCreated}
        filterLimits={filterLimits}
        actionListStates={actionListStates}
        isAllStatusesSelected={isAllStatusesSelected}
        setIsOpen={setShowAdditionalFilter}
      />
    </div>
  );
};

export default FilterPanel;
